import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  FormControlLabel,
  Typography,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Divider,
  TextField,
  InputAdornment,
  IconButton,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { adminApi, FeatureDefinition, FeatureCategory } from '../services/api';
import { matchesFeatureQuery, isFiltering } from './featureSearch';

interface FeatureSelectorProps {
  selectedFeatures: string[];
  onChange: (features: string[]) => void;
  disabled?: boolean;
}

const FeatureSelector: React.FC<FeatureSelectorProps> = ({
  selectedFeatures,
  onChange,
  disabled = false,
}) => {
  const [features, setFeatures] = useState<FeatureDefinition[]>([]);
  const [byCategory, setByCategory] = useState<Record<FeatureCategory, FeatureDefinition[]>>({} as any);
  const [proprietaryFeatures, setProprietaryFeatures] = useState<FeatureDefinition[]>([]);
  const [categoryLabels, setCategoryLabels] = useState<Record<FeatureCategory, string>>({} as any);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [invalidFeatures, setInvalidFeatures] = useState<string[]>([]);
  const [query, setQuery] = useState('');

  const filtering = isFiltering(query);

  /**
   * Which categories the user has collapsed/expanded by hand.
   *
   * Every Accordion below is FULLY CONTROLLED off this map. The first version
   * passed `expanded` only while filtering and relied on `defaultExpanded`
   * otherwise, which flips each Accordion between controlled and uncontrolled
   * on the first keystroke — React warns about exactly that, and the component
   * loses the user's manual expansion state at the same moment.
   *
   * Absent from the map means "use the default for this section": general
   * categories open, proprietary closed, matching how the dialog behaved
   * before the search box existed.
   */
  const [expandedOverrides, setExpandedOverrides] = useState<Record<string, boolean>>({});

  const isExpanded = (category: string, defaultOpen: boolean) => {
    // A filter forces every surviving category open — a match must never hide
    // behind a collapsed header. The user's own toggles are remembered and
    // reinstated the moment the box is cleared.
    if (filtering) return true;
    return expandedOverrides[category] ?? defaultOpen;
  };

  const toggleCategory = (category: string, defaultOpen: boolean) => {
    if (filtering) return; // headers are inert while filtering — see above
    setExpandedOverrides((prev) => ({
      ...prev,
      [category]: !(prev[category] ?? defaultOpen),
    }));
  };

  /**
   * The visible feature set, per category, after the search box.
   *
   * Memoised on the query rather than recomputed inline: this runs for every
   * category on every keystroke, and the category lists are the same objects
   * each time.
   */
  const visibleByCategory = useMemo(() => {
    if (!filtering) return byCategory;
    const out = {} as Record<FeatureCategory, FeatureDefinition[]>;
    for (const [category, categoryFeatures] of Object.entries(byCategory)) {
      const label = categoryLabels[category as FeatureCategory] || category;
      out[category as FeatureCategory] = (categoryFeatures as FeatureDefinition[]).filter((f) =>
        matchesFeatureQuery(f, query, label),
      );
    }
    return out;
  }, [byCategory, categoryLabels, query, filtering]);

  /** How many features the query is showing, across every category. */
  const matchCount = useMemo(
    () => Object.values(visibleByCategory).reduce((n, list) => n + (list as FeatureDefinition[]).length, 0),
    [visibleByCategory],
  );

  useEffect(() => {
    const fetchFeatures = async () => {
      try {
        setLoading(true);
        const response = await adminApi.listFeatures();
        setFeatures(response.data.features);
        setByCategory(response.data.byCategory);
        setProprietaryFeatures(response.data.byVisibility.proprietary);
        setCategoryLabels(response.data.categoryLabels);

        // Auto-clean deprecated/invalid features from selection
        const allKnownIds = new Set(response.data.features.map((f: FeatureDefinition) => String(f.id)));
        const deprecated = selectedFeatures.filter((id) => !allKnownIds.has(id));
        if (deprecated.length > 0) {
          setInvalidFeatures(deprecated);
          // Strip deprecated features so they won't be saved back
          onChange(selectedFeatures.filter((id) => allKnownIds.has(id)));
        }
      } catch (err: any) {
        setError(err.response?.data?.message || err.message || 'Failed to load features');
      } finally {
        setLoading(false);
      }
    };

    fetchFeatures();
  }, []);

  const handleFeatureToggle = (featureId: string) => {
    if (selectedFeatures.includes(featureId)) {
      onChange(selectedFeatures.filter((id) => id !== featureId));
    } else {
      onChange([...selectedFeatures, featureId]);
    }
  };


  /**
   * Select All / Deselect All act on the features CURRENTLY SHOWN, not on the
   * whole category.
   *
   * These used to read `byCategory[category]` directly, which was correct while
   * everything was always visible. With a search box it is not: pressing
   * "Select All" under a filter would tick features the user cannot see and did
   * not mean to grant — silently, since the confirmation is a count they are
   * not looking at. Passing the visible list in makes the button do exactly
   * what is on screen.
   */
  const handleSelectAllShown = (shown: FeatureDefinition[]) => {
    const shownIds = shown.map((f) => String(f.id));
    const otherIds = selectedFeatures.filter((id) => !shownIds.includes(id));
    onChange([...otherIds, ...shownIds]);
  };

  const handleDeselectAllShown = (shown: FeatureDefinition[]) => {
    const shownIds = shown.map((f) => String(f.id));
    onChange(selectedFeatures.filter((id) => !shownIds.includes(id)));
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  // Group categories by visibility. Driven by the FILTERED lists, so a category
  // with no matches drops out of the dialog entirely rather than sitting there
  // as an empty accordion the user still has to scan past.
  const generalCategories = Object.entries(visibleByCategory).filter(([_, features]) =>
    (features as FeatureDefinition[]).some((f) => f.visibility === 'GENERAL')
  );
  const proprietaryCategories = Object.entries(visibleByCategory).filter(([_, features]) =>
    (features as FeatureDefinition[]).some((f) => f.visibility === 'PROPRIETARY')
  );

  return (
    <Box>
      {/* Search — sticky, because the list is ~71 checkboxes over nine
          categories and a box that scrolls away is a box you cannot correct a
          typo in. */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 2,
          bgcolor: 'background.paper',
          pt: 0.5,
          pb: 1.5,
        }}
      >
        <TextField
          fullWidth
          size="small"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          disabled={disabled}
          placeholder="Search features — name, description, or id (e.g. REPORT_TRANSFORM)"
          inputProps={{ 'aria-label': 'Search features' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: query ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  aria-label="Clear search"
                  onClick={() => setQuery('')}
                  edge="end"
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
        {filtering && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
            {matchCount === 0
              ? `Nothing matches "${query}".`
              : `${matchCount} of ${features.length} features shown. Categories with no match are hidden.`}
          </Typography>
        )}
      </Box>

      {/* An explicit empty state. Without it a query that matches nothing looks
          identical to the features having failed to load. */}
      {filtering && matchCount === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No feature matches <strong>{query}</strong>. Try part of the name, or the
          feature id as it appears in the backend.
        </Alert>
      )}
      {/* Warning for deprecated features that were auto-removed */}
      {invalidFeatures.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          The following deprecated feature(s) were removed from the selection: <strong>{invalidFeatures.join(', ')}</strong>. 
          They will be cleaned up when you save.
        </Alert>
      )}

      {/* General Features Section */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: 1.5 }}>
            General Features
          </Typography>
        </Box>

        {generalCategories.map(([category, categoryFeatures]) => {
          const categoryKey = category as FeatureCategory;
          const generalFeaturesInCategory = categoryFeatures.filter(
            (f) => f.visibility === 'GENERAL'
          );

          if (generalFeaturesInCategory.length === 0) return null;

          const selectedInCategory = generalFeaturesInCategory.filter((f) =>
            selectedFeatures.includes(f.id)
          ).length;

          const allShownSelected = selectedInCategory === generalFeaturesInCategory.length;

          return (
            // Fully controlled — see `expandedOverrides`. General categories
            // default open; a filter forces them open and makes the header
            // inert.
            <Accordion
              key={category}
              expanded={isExpanded(category, true)}
              onChange={() => toggleCategory(category, true)}
            >
              <AccordionSummary expandIcon={filtering ? null : <ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                  <Typography sx={{ fontWeight: 600 }}>
                    {categoryLabels[categoryKey] || category}
                  </Typography>
                  <Chip
                    label={`${selectedInCategory}/${generalFeaturesInCategory.length}`}
                    size="small"
                    color={allShownSelected ? 'success' : 'default'}
                  />
                  {filtering && (
                    <Typography variant="caption" color="text.secondary">
                      matching
                    </Typography>
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                  <Button
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (allShownSelected) {
                        handleDeselectAllShown(generalFeaturesInCategory);
                      } else {
                        handleSelectAllShown(generalFeaturesInCategory);
                      }
                    }}
                    disabled={disabled}
                  >
                    {/* Says "Shown" under a filter so nobody reads it as
                        "everything in this category". */}
                    {allShownSelected
                      ? (filtering ? 'Deselect Shown' : 'Deselect All')
                      : (filtering ? 'Select Shown' : 'Select All')}
                  </Button>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {generalFeaturesInCategory.map((feature) => (
                    <Box key={String(feature.id)} sx={{ pl: 1 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selectedFeatures.includes(String(feature.id))}
                            onChange={() => handleFeatureToggle(String(feature.id))}
                            disabled={disabled}
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {feature.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {feature.description}
                            </Typography>
                            {/* The id, shown only while searching. It is what
                                someone was given ("enable REPORT_TRANSFORM")
                                and it is not the display name, so a row that
                                matched on the id must SAY so — otherwise the
                                result looks arbitrary. Hidden at rest because
                                71 ids is noise nobody reads. */}
                            {filtering && (
                              <Typography
                                variant="caption"
                                color="text.disabled"
                                sx={{ display: 'block', fontFamily: 'monospace' }}
                              >
                                {String(feature.id)}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                    </Box>
                  ))}
                </Box>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Box>

      {/* Proprietary Features Section */}
      {proprietaryFeatures.length > 0 && (
        <Box>
          <Divider sx={{ my: 3 }} />
          <Box sx={{ mb: 2 }}>
            <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: 1.5 }}>
              Proprietary Features
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              Organization-specific features (disabled by default)
            </Typography>
          </Box>

          {proprietaryCategories.map(([category, categoryFeatures]) => {
            const categoryKey = category as FeatureCategory;
            const proprietaryFeaturesInCategory = categoryFeatures.filter(
              (f) => f.visibility === 'PROPRIETARY'
            );

            if (proprietaryFeaturesInCategory.length === 0) return null;

            const selectedInCategory = proprietaryFeaturesInCategory.filter((f) =>
              selectedFeatures.includes(f.id)
            ).length;

            return (
              // Same controlled treatment as the general half, but defaulting
              // CLOSED. Without the forced expansion a query whose only match
              // lives in here would show an empty dialog while the count said
              // otherwise.
              <Accordion
                key={category}
                expanded={isExpanded(category, false)}
                onChange={() => toggleCategory(category, false)}
              >
                <AccordionSummary expandIcon={filtering ? null : <ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                    <Typography sx={{ fontWeight: 600 }}>
                      {categoryLabels[categoryKey] || category}
                    </Typography>
                    <Chip
                      label={`${selectedInCategory}/${proprietaryFeaturesInCategory.length}`}
                      size="small"
                      color={selectedInCategory > 0 ? 'primary' : 'default'}
                      variant="outlined"
                    />
                    {filtering && (
                      <Typography variant="caption" color="text.secondary">
                        matching
                      </Typography>
                    )}
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {proprietaryFeaturesInCategory.map((feature) => (
                      <Box key={String(feature.id)} sx={{ pl: 1 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={selectedFeatures.includes(String(feature.id))}
                              onChange={() => handleFeatureToggle(String(feature.id))}
                              disabled={disabled}
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {feature.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {feature.description}
                              </Typography>
                              {filtering && (
                                <Typography
                                  variant="caption"
                                  color="text.disabled"
                                  sx={{ display: 'block', fontFamily: 'monospace' }}
                                >
                                  {String(feature.id)}
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                      </Box>
                    ))}
                  </Box>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>
      )}

      {/* Summary — always the TOTAL selected, never the filtered subset. This
          number is what the user checks before pressing Save, and a count that
          silently meant "of the ones currently shown" would be the most
          expensive possible place to be clever. */}
      <Box sx={{ mt: 3, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
        <Typography variant="body2" color="text.secondary">
          <strong>{selectedFeatures.length}</strong> of <strong>{features.length}</strong> features selected
          {filtering && ' (unaffected by the search above)'}
        </Typography>
      </Box>
    </Box>
  );
};

export default FeatureSelector;
